import React, { useState, useRef } from 'react';
import { Upload, Download, Trash2, FileText, Loader2, X, Paperclip } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { useFetch, Spinner } from '../lib/useFetch';
import { uploadsApi } from '../lib/api';

const formatBytes = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Modale allegati specifica per una singola visita medica:
 * carica file taggati con entityType='visite', entityId=visita.id,
 * workerId=visita.workerId. Non si mescolano con la formazione.
 */
const VisitaAttachmentsDialog = ({ open, onOpenChange, visita, workerName }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { data: allAttachments, loading, reload } = useFetch(
    () => (visita?.id ? uploadsApi.list({ entityType: 'visite', entityId: visita.id }) : Promise.resolve({ data: [] })),
    [visita?.id, open],
  );

  const attachments = (allAttachments || []).filter((a) => a.entityId === visita?.id);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !visita) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entityType', 'visite');
      fd.append('entityId', visita.id);
      fd.append('companyId', visita.companyId);
      if (visita.workerId) fd.append('workerId', visita.workerId);
      await uploadsApi.upload(fd);
      toast.success('Allegato caricato');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Eliminare "${a.originalName}"?`)) return;
    try { await uploadsApi.remove(a.id); toast.success('Allegato eliminato'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const onDownload = async (a) => {
    try { await uploadsApi.download(a.id); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore download'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="visita-attachments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#023F80]">
            <Paperclip size={18} /> Allegati visita medica
          </DialogTitle>
          <DialogDescription>
            {workerName ? <span className="font-semibold">{workerName}</span> : null}
            {visita?.dataVisita ? <> · {new Date(visita.dataVisita).toLocaleDateString('it-IT')}</> : null}
            {visita?.tipoVisita ? <> · {visita.tipoVisita}</> : null}
            <div className="text-xs text-slate-500 mt-1">
              Gli allegati caricati qui sono associati SOLO a questa visita — non compaiono nella sezione formazione.
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              onChange={onFile}
              disabled={uploading}
              data-testid="visita-upload-input"
              className="text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#fef7d4] file:text-[#8a6608] hover:file:bg-[#E6B800]"
            />
            {uploading && <span className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> caricamento…</span>}
          </div>

          {loading ? <Spinner /> : (attachments.length === 0 ? (
            <div className="py-8 text-center border-2 border-dashed border-slate-200 rounded-xl">
              <FileText size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">Nessun allegato per questa visita.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {attachments.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:bg-slate-50" data-testid={`visita-attachment-${a.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-[#e0e7ff] text-[#023F80] flex items-center justify-center shrink-0">
                    <FileText size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 text-sm truncate">{a.originalName}</div>
                    <div className="text-[11px] text-slate-500 flex gap-3">
                      <span>{formatBytes(a.sizeBytes)}</span>
                      <span>{new Date(a.uploadedAt).toLocaleString('it-IT')}</span>
                    </div>
                  </div>
                  <button onClick={() => onDownload(a)} title="Scarica" data-testid={`visita-att-dl-${a.id}`}
                          className="p-1.5 rounded-md hover:bg-slate-100">
                    <Download size={14} className="text-slate-600" />
                  </button>
                  <button onClick={() => onDelete(a)} title="Elimina" data-testid={`visita-att-del-${a.id}`}
                          className="p-1.5 rounded-md hover:bg-red-50">
                    <Trash2 size={14} className="text-red-600" />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} data-testid="visita-att-close"
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 hover:bg-slate-200">
            Chiudi
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VisitaAttachmentsDialog;
